import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { FaPhoneAlt } from "react-icons/fa";
import emailjs from "emailjs-com";

const Contact = () => {
  const [activeTab, setActiveTab] = useState("service");

  return (
    <div className="w-full min-h-screen text-gray-800">
      {/* Hero Section */}
      <section className="bg-[#c4122f] text-white py-16 px-6 md:px-20">
        <h1 className="text-4xl md:text-5xl font-bold">Contact Us</h1>
        <p className="mt-2 text-lg md:text-xl">
          What can ARGUS Security International Inc. do for you?
        </p>
        <div className="flex flex-wrap gap-4 mt-6">
          <button
            onClick={() => setActiveTab("service")}
            className={`px-6 py-2 rounded-full transition ${
              activeTab === "service"
                ? "bg-white text-[#c4122f] font-semibold"
                : "bg-transparent border border-white"
            }`}
          >
            Service Inquiries
          </button>
          <button
            onClick={() => setActiveTab("general")}
            className={`px-6 py-2 rounded-full transition ${
              activeTab === "general"
                ? "bg-white text-[#c4122f] font-semibold"
                : "bg-transparent border border-white"
            }`}
          >
            General Inquiries
          </button>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-10 px-6 md:px-20">
        <div className="flex justify-end text-[#c4122f] items-center gap-2 mb-4">
          <FaPhoneAlt />
          <span className="text-sm md:text-base">+1 (416) 477-7692</span>
        </div>

        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="bg-gray-50 p-8 rounded-2xl shadow-lg"
        >
          <h2 className="text-2xl font-bold text-[#c4122f] mb-4">
            {activeTab === "service" ? "Service Inquiry" : "General Inquiry"}
          </h2>
          <p className="text-gray-600 mb-8">
            {activeTab === "service"
              ? "Fill out the form and our Sales Team will contact you shortly."
              : "Let us know how we can help you and we’ll be in touch soon."}
          </p>
          <FormSection formType={activeTab} />
        </motion.div>
      </section>
    </div>
  );
};

const FormSection = ({ formType }) => {
  const isService = formType === "service";
  const formRef = useRef();
  const [isSubmitted, setIsSubmitted] = useState(false);

  const sendEmail = (e) => {
    e.preventDefault();

    const form = formRef.current;

    emailjs
      .sendForm(
        "service_gqq3r0s", // replace with your service ID
        "template_n7xykcq", // template for admin
        form,
        "_UlWobspuHVWZJ3LQ" // public key from EmailJS
      )
      .then(
        (result) => {
          console.log("Admin email sent:", result.text);
          // Send client confirmation
          emailjs.sendForm(
            "service_gqq3r0s",
            "template_iyhd9c4", // template for client thank you
            form,
            "_UlWobspuHVWZJ3LQ"
          );
          setIsSubmitted(true);
          form.reset();
        },
        (error) => {
          console.error("Error:", error.text);
        }
      );
  };

  const Input = ({ name, placeholder, required = false, type = "text" }) => (
    <div className="group relative w-full">
      <input
        type={type}
        name={name}
        required={required}
        placeholder=" "
        className="peer w-full rounded-xl border border-gray-300 bg-white px-4 pt-5 pb-2 text-sm text-gray-900 placeholder-transparent shadow-sm transition-all duration-300 focus:border-[#c4122f] focus:outline-none focus:ring-2 focus:ring-[#c4122f]"
      />
      <label className="absolute left-4 top-2.5 text-xs text-gray-500 transition-all peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400 peer-focus:top-2.5 peer-focus:text-xs peer-focus:text-[#c4122f]">
        {placeholder}
      </label>
    </div>
  );

  return isSubmitted ? (
    <div className="text-green-600 font-semibold text-lg">
      Thank you! Your inquiry has been submitted.
    </div>
  ) : (
    <form
      ref={formRef}
      onSubmit={sendEmail}
      className="grid grid-cols-1 md:grid-cols-2 gap-6"
    >
      <Input name="first_name" placeholder="First Name *" required />
      <Input name="last_name" placeholder="Last Name" />

      {isService && (
        <>
          <Input name="title" placeholder="Title" />
          <Input name="company" placeholder="Company Name" />
        </>
      )}

      <Input name="email" placeholder="Email *" type="email" required />
      <Input name="phone" placeholder="Phone Number *" required />

      {isService && (
        <>
          <Input name="office_phone" placeholder="Office Phone" />
          <Input name="city" placeholder="City" />
          <Input name="province" placeholder="Province" />
          <Input name="industry" placeholder="Industry" />
          <Input name="service_required" placeholder="Service Required" />
          <Input name="referral" placeholder="How did you hear about us?" />
        </>
      )}

      <div className="md:col-span-2">
        <div className="group relative">
          <textarea
            name="message"
            placeholder=" "
            required
            rows="4"
            className="peer w-full rounded-xl border border-gray-300 bg-white px-4 pt-5 pb-2 text-sm text-gray-900 placeholder-transparent shadow-sm transition-all duration-300 focus:border-[#c4122f] focus:outline-none focus:ring-2 focus:ring-[#c4122f]"
          ></textarea>
          <label className="absolute left-4 top-2.5 text-xs text-gray-500 transition-all peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400 peer-focus:top-2.5 peer-focus:text-xs peer-focus:text-[#c4122f]">
            {isService ? "Additional Comments" : "Reason for Enquiry *"}
          </label>
        </div>
      </div>

      <div className="md:col-span-2">
        <button
          type="submit"
          className="w-full md:w-auto px-6 py-3 bg-[#c4122f] text-white font-medium rounded-xl hover:bg-red-700 transition"
        >
          Submit
        </button>
      </div>
    </form>
  );
};

export default Contact;
